import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Star, MapPin, ShieldCheck, FileText, Check, Inbox, Rocket } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EntityPage from "@/components/EntityPage";
import { PageHeader, Mocked, Badge2 } from "@/components/common";
import RfqWorkflow, { RevisionButton } from "@/components/RfqWorkflow";
import { useMeta, MetaConnectCard } from "@/components/MetaPanel";
import UnifiedBuilder from "@/components/am/UnifiedBuilder";

export function Campaigns() {
  const { can } = useAuth();
  const [meta, reloadMeta] = useMeta();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const m = params.get("meta");
    if (!m) return;
    const R = { denied: "You cancelled the Meta login or did not allow access.", invalid_state: "This Meta login could not be verified (it was started in another browser or tab). Please press Connect again.",
      expired: "The Meta login link expired. Please press Connect again.", exchange_failed: "Meta rejected the login. Check the app settings or try again.",
      system_user_token: "Meta returned a System User token, not a User access token. In your Meta app, use a Login Configuration with token type 'User access token', then Reconnect." };
    if (m === "connected") toast.success("Meta connected. Confirm the accounts we found below.");
    if (m === "error") toast.error(R[params.get("reason")] || "Meta connection failed. Please try again.");
    setParams({}, { replace: true });
  }, [params, setParams]);
  return (
    <div className="space-y-6">
      <PageHeader label="Market" title="Campaign Center" subtitle="The one place to create campaigns. Build Campaign → Ad set → Ad, review, and create them on your own Meta ad account.">
        <Link to="/ads-manager"><Button variant="secondary" data-testid="manage-meta-campaigns-btn"><Rocket className="h-4 w-4 mr-1" />Manage existing Meta campaigns</Button></Link>
      </PageHeader>
      <MetaConnectCard meta={meta} reload={reloadMeta} canWrite={can("marketing", "write")} />
      <UnifiedBuilder />
      <details className="rounded-xl border border-slate-800 p-4" data-testid="legacy-campaigns">
        <summary className="cursor-pointer text-sm font-semibold">Earlier campaign records (view only)</summary>
        <p className="text-xs text-slate-500 my-2">Records saved before the unified builder. They are kept for reference and cannot be created or launched from here. Create new campaigns with “Create campaign” above.</p>
        <EntityPage entity="campaigns" readOnly />
      </details>
    </div>
  );
}

export function Vendors() {
  const { schema, me } = useAuth();
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("All");
  const shown = rows.filter((v) => cat === "All" || v.category === cat);
  return (
    <div data-testid="vendors-page">
      <PageHeader label="Launch" title="Vendor Marketplace" subtitle="Verified offline media & event vendors. Request quotes directly through the RFQ Center.">
        <Link to="/rfqs"><Button data-testid="vendors-create-rfq-button"><FileText className="h-4 w-4 mr-1" />Create RFQ</Button></Link>
      </PageHeader>
      <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-2 mb-6">
        {["All", ...(schema?.vendor_categories || [])].map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`vendor-category-${c.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-4 py-1.5 rounded-full text-sm border whitespace-nowrap transition-colors ${cat === c ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-800 text-slate-400 hover:border-slate-700"}`}>{c}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-10">
        {shown.map((v) => (
          <div key={v.id} className="lx-card" data-testid={`vendor-card-${v.id}`}>
            <div className="flex justify-between items-start"><Badge2 tone="violet">{v.category}</Badge2>{v.verified === "Yes" && <ShieldCheck className="h-4 w-4 text-emerald-400" />}</div>
            <div className="font-semibold mt-3">{v.name}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" />{v.city}</div>
            <div className="flex justify-between items-center mt-4 text-sm"><span className="font-mono text-slate-300">{v.rate_card}</span><span className="flex items-center gap-1 text-amber-400"><Star className="h-3.5 w-3.5 fill-amber-400" />{v.rating}</span></div>
          </div>
        ))}
      </div>
      <div className={me?.is_super_admin ? "" : "hidden"}><EntityPage entity="vendors" onRows={setRows} /></div>
    </div>
  );
}

function QuotesDialog({ rfq, onClose, reload }) {
  const { can } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState({ vendor_id: "", amount: "", timeline: "" });
  const [data, setData] = useState(rfq);
  const refresh = async () => { const r = await api.get(`/rfqs/${rfq.id}`); setData(r.data); reload(); };
  useEffect(() => { api.get("/vendors", { params: { category: rfq.category } }).then((r) => setVendors(r.data)); }, [rfq.category]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const quotes = data.quotes || [];
  const min = Math.min(...quotes.map((x) => x.amount));
  const w = can("marketing", "write") && data.status !== "Approved";
  const ww = can("marketing", "write");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-slate-800" data-testid="rfq-quotes-dialog">
        <DialogHeader><DialogTitle>{data.title}</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-400">{data.category} · Budget {inr(data.budget)} · Deadline {data.deadline}</div>
        <RfqWorkflow data={data} refresh={refresh} w={ww} />
        {w && (
          <div className="flex flex-wrap gap-2 items-end">
            <Button variant="ghost" size="sm" onClick={() => run(() => api.post(`/rfqs/${rfq.id}/invite`), "Demo quotes added")} data-testid="rfq-invite-vendors-button"><Inbox className="h-4 w-4 mr-1" />Add demo quotes</Button>
            <Mocked what="DEMO QUOTES ARE SIMULATED" />
          </div>
        )}
        {w && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <Select value={q.vendor_id} onValueChange={(v) => setQ({ ...q, vendor_id: v })}><SelectTrigger data-testid="rfq-quote-vendor-select"><SelectValue placeholder="Vendor" /></SelectTrigger><SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select>
            <Input placeholder="Amount (INR)" type="number" value={q.amount} onChange={(e) => setQ({ ...q, amount: e.target.value })} data-testid="rfq-quote-amount-input" />
            <Input placeholder="Timeline" value={q.timeline} onChange={(e) => setQ({ ...q, timeline: e.target.value })} data-testid="rfq-quote-timeline-input" />
            <Button onClick={() => run(() => api.post(`/rfqs/${rfq.id}/quotes`, { ...q, amount: Number(q.amount) }), "Quote added")} disabled={!q.vendor_id || !q.amount} data-testid="rfq-add-quote-button">Add quote</Button>
          </div>
        )}
        <table className="w-full text-sm mt-2" data-testid="rfq-quotes-table">
          <thead className="text-[11px] uppercase text-slate-500"><tr><th className="text-left py-2">Vendor</th><th className="text-left">Rating</th><th className="text-left">Amount</th><th className="text-left">Timeline</th><th className="text-left">Status</th><th /></tr></thead>
          <tbody>
            {quotes.map((x) => (
              <tr key={x.id} className="border-t border-slate-800">
                <td className="py-2">{x.vendor_name}{x.amount === min && quotes.length > 1 && <span className="ml-2"><Badge2 tone="green">Lowest</Badge2></span>}
                  <div className="text-[10px] text-slate-500">{x.source === "vendor_portal" ? `Vendor portal v${x.version} · ${x.contact_email}${x.total ? ` · incl. tax ${inr(x.total)}` : ""}${x.inclusions ? ` · ${x.inclusions.slice(0, 60)}` : ""}` : String(x.notes || "").includes("MOCKED") ? "SIMULATED demo quote" : "Entered manually"}</div></td>
                <td>{x.rating}</td><td className="font-mono">{inr(x.amount)}</td><td>{x.timeline}</td><td><Badge2>{x.status}</Badge2></td>
                <td className="text-right">{w && <RevisionButton rid={rfq.id} q={x} refresh={refresh} />}{w && <Button size="sm" onClick={() => run(() => api.post(`/rfqs/${rfq.id}/approve/${x.id}`), "Vendor approved")} data-testid={`rfq-approve-${x.id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>}</td>
              </tr>
            ))}
            {!quotes.length && <tr><td colSpan={6} className="py-6 text-center text-slate-500">No quotes yet</td></tr>}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}

export function RFQs() {
  const [params] = useSearchParams();
  const [open, setOpen] = useState(null);
  return (
    <div>
      <PageHeader label="Launch" title="RFQ Center" subtitle="Create RFQs, receive vendor quotes, compare side-by-side and approve. Approved quotes post to Vendor Spend automatically." />
      <EntityPage entity="rfqs" defaults={{ status: "Open", category: params.get("category") || undefined }}
        rowActions={(r, reload) => <Button size="sm" variant="secondary" className="mr-1" onClick={() => setOpen({ r, reload })} data-testid={`rfq-quotes-${r.id}`}>Quotes ({(r.quotes || []).length})</Button>} />
      {open && <QuotesDialog rfq={open.r} reload={open.reload} onClose={() => setOpen(null)} />}
    </div>
  );
}
